// components/AnnouncementBanner.tsx
// 管理画面（/admin/announcement）で設定したお知らせ文言を、一般ユーザー全員に表示する
// バナー。認証不要のGET /announcementを叩くだけ（is_active=falseなら何も描画しない）。
//
// 2026-08-26: 当初は通常フローの先頭（Headerより前）に置いていたが、HeaderもGraphPageHeaderも
// position="fixed"で画面上部に固定表示されているため、通常フローのバナーは完全にその裏へ
// 隠れてしまい表示されなかった（APIは正しく動いていたが見た目上は何も出ていないように見えた）。
// 既存の全ページがヘッダー分の上マージンを個別に持っており、そこに割り込むのは影響範囲が
// 大きいため、バナー自体を画面下部に固定表示するトースト的な方式に変更した。

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useAppTokens } from '../context/ThemeModeContext';
import { NEW_API } from '../config/api';

interface AnnouncementData {
  is_active: boolean;
  message?: string;
  severity?: 'info' | 'warning';
}

const DISMISSED_KEY_PREFIX = 'dismissedAnnouncement_';

const AnnouncementBanner: React.FC = () => {
  const TOKENS = useAppTokens();
  const [announcement, setAnnouncement] = useState<AnnouncementData | null>(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(NEW_API.ANNOUNCEMENT)
      .then((res) => {
        if (cancelled) return;
        const data: AnnouncementData = res.data?.data ?? { is_active: false };
        setAnnouncement(data);
        if (data.is_active && data.message) {
          // 同じ文言を閉じたことがあるユーザーには再表示しない
          const key = `${DISMISSED_KEY_PREFIX}${data.message}`;
          setDismissed(window.localStorage.getItem(key) === '1');
        }
      })
      .catch(() => {
        // お知らせ取得の失敗はページ表示をブロックしない
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!announcement?.is_active || !announcement.message || dismissed) return null;

  const isWarning = announcement.severity === 'warning';

  const handleDismiss = () => {
    if (announcement.message) {
      window.localStorage.setItem(`${DISMISSED_KEY_PREFIX}${announcement.message}`, '1');
    }
    setDismissed(true);
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        // Header/GraphPageHeaderはposition="fixed"でtheme.zIndex.drawer+1を使っているため、
        // それより確実に上に来るよう明示的に高いz-indexを指定する
        zIndex: (theme) => theme.zIndex.drawer + 10,
        background: isWarning ? '#5c3a00' : TOKENS.glassStrong,
        borderTop: `1px solid ${isWarning ? '#8a5a00' : TOKENS.border}`,
        color: isWarning ? '#ffe4b3' : TOKENS.text,
        px: 2,
        py: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1.5,
        textAlign: 'center',
        boxShadow: '0 -2px 12px rgba(0,0,0,0.25)',
      }}
    >
      <Typography sx={{ fontSize: 12.5 }}>{announcement.message}</Typography>
      <Box
        component="button"
        onClick={handleDismiss}
        sx={{
          fontSize: 11.5,
          color: 'inherit',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          textDecoration: 'underline',
          opacity: 0.8,
          flexShrink: 0,
        }}
      >
        閉じる
      </Box>
    </Box>
  );
};

export default AnnouncementBanner;
